import AFRAME from 'aframe';
import {
  isIphone,
  isSafari,
  iOSMotionEnabled,
  isIOS13,
} from '../../../utils/userAgentHelpers';
import { customGaEvent, GA_EVENTS } from '../../../utils/ga';

export const FADE_OUT_TIME = 600;
export const FADE_PAUSE_TIME = 200;
export const FADE_IN_TIME = 600;

const positionCameraNearTarget = (position, isPano) => {
  const target = new AFRAME.THREE.Vector3(0, 0, 0);
  if (target.distanceTo(position) < 0.4) {
    return position;
  }
  // target used to be at 1.5.  This makes up for it if distance is beyond 0.4
  const panoPosition = new AFRAME.THREE.Vector3(
    position.x,
    position.y - 1.5,
    position.z
  );
  return new AFRAME.THREE.Vector3()
    .subVectors(isPano ? panoPosition : position, target)
    .setLength(0.15)
    .add(target);
};

const getWorldPosition = entity => {
  return entity?.object3D?.getWorldPosition(new AFRAME.THREE.Vector3());
};

export const setCamera = (camera, { rotation, position }, isPano) => {
  const newPosition = positionCameraNearTarget(position, isPano);
  camera.setAttribute('orbit-controls', 'enabled', false);
  newPosition && camera.setAttribute('position', newPosition);
  rotation && camera.setAttribute('rotation', rotation);
  // necessary for switching stops after opening a hotspot
  camera.setAttribute('orbit-controls', 'rotateTo', getWorldPosition(camera));
  camera.setAttribute('orbit-controls', 'enabled', true);
};

export const modifyFooterClass = add => {
  const footer = document.getElementById('footer');
  if (add) {
    footer?.classList?.add('vr-mode');
  } else {
    footer?.classList?.remove('vr-mode');
  }
};

export const getScene = () => {
  return document.querySelector('a-scene');
};

export const handleIosSafariOrientationSwap = () => {
  setTimeout(() => {
    resizeAframeScene();
  }, 500);
};

export const resizeAframeScene = () => {
  const scene = getScene();
  if (!scene) return;
  scene.renderer.setSize(window.innerWidth, window.innerHeight);
  if (scene.camera) {
    scene.camera.aspect = window.innerWidth / window.innerHeight;
    scene.camera.updateProjectionMatrix();
  }
};

export const modifyCanvasClass = add => {
  const canvas = document.querySelector('.a-canvas');
  if (add) {
    canvas.classList.add('vr-mode');
  } else {
    canvas.classList.remove('vr-mode');
  }
};

export const exitVRMode = (scene, tourData) => {
  if (scene && scene.hasLoaded) {
    modifyFooterClass(false);
    modifyCanvasClass(false);
    tourData &&
      customGaEvent({
        action: GA_EVENTS.VR_MODE,
        label: 'exited VR',
      });
    scene.exitVR();
    return true;
  }
  return false;
};

export const enterVR = (scene, tourData) => {
  if (scene && scene.hasLoaded) {
    modifyFooterClass(true);
    modifyCanvasClass(true);
    tourData &&
      customGaEvent({
        action: GA_EVENTS.VR_MODE,
        label: 'entered VR',
      });
    scene.enterVR();
    return true;
  }
  return false;
};

export const enterVRMode = (tourData, history, scene) => {
  if (isIphone() && isSafari()) {
    // EDGE CASE - https://concept3d.atlassian.net/browse/TB-682
    // iOS 13 changed mobile device orientation permissions
    if (isIOS13()) {
      DeviceMotionEvent.requestPermission().then(response => {
        if (response !== 'granted') {
          history.push({
            pathname: '/share/error/ios-13-device-motion',
            oldLocation: history.location,
          });
        } else {
          enterVR(scene, tourData);
        }
      });
    } else {
      // need to comment this out and simply call enterVR(scene, tourData) if using motion controls on chrome
      iOSMotionEnabled().then(res => {
        if (res === undefined || !res) {
          history.push({
            pathname: '/share/error/ios-device-motion',
            oldLocation: history.location,
          });
        } else {
          enterVR(scene, tourData);
        }
      });
    }
  } else {
    enterVR(scene, tourData);
  }
};

export const toggleFullScreen = (tourData, aid) => {
  const scene = document.querySelector('#scene');
  
  const isFullscreen = 
    document.fullscreen || 
    document.webkitCurrentFullScreenElement || 
    document.mozFullScreenElement || 
    document.msFullscreenElement;

  if (isFullscreen) {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
    
    if (tourData) {
      customGaEvent({
        action: GA_EVENTS.FULL_SCREEN,
        label: 'exited fullscreen',
      });
    }
  } else {
    if (scene && scene.requestFullScreen) {
      scene.requestFullScreen();
      fireGoogleAnalyticsEvent(tourData, aid);
    } else if (scene && scene.webkitRequestFullScreen) {
      fireGoogleAnalyticsEvent(tourData, aid);
      scene.webkitRequestFullScreen();
    } else if (scene && scene.mozRequestFullScreen) {
      fireGoogleAnalyticsEvent(tourData, aid);
      scene.mozRequestFullScreen();
    } else if (scene && scene.msRequestFullscreen) {
      scene.msRequestFullscreen();
      fireGoogleAnalyticsEvent(tourData, aid);
    }
  }
};

const fireGoogleAnalyticsEvent = tourData => {
  if (tourData) {
    customGaEvent({
      action: GA_EVENTS.FULL_SCREEN,
      label: 'clicked full screen',
    });
  }
};

const AframeShared = {
  exitVRMode,
  enterVRMode,
  toggleFullScreen,
  modifyFooterClass,
  modifyCanvasClass,
  FADE_OUT_TIME,
  FADE_PAUSE_TIME,
  FADE_IN_TIME,
};

export default AframeShared;
